import { useRouter } from 'next/router';
import isEqual from 'lodash/isEqual';
import { getContainer } from '@framework/di/getContainer';
import createAction from '@framework/state/createAction';
import CreditFlowResearchRepository from '@/creditflowresearch/infrastructure/repository/CreditFlowResearchRepository';
import {
  IMarketArticlesFilterBoundaryResponse,
  IMarketArticlesFilterPayload,
} from '@/creditflowresearch/domain/vo/ArticlesAndStats/Articles/IMarketArticle';
import {
  GET_FILTERS,
  GET_FILTERS_SUCCESS,
  INITIAL_FILTERS,
  FILTER_APPLY,
  FILTER_MIN_DATE_FROM,
  FILTER_MAX_DATE_TO,
  GET_FILTERS_ERROR,
  FILTER_PAYLOAD,
} from './actionTypes';
import Logger from '@framework/sentry/Logger';
import { initialArticlesFilterState } from '@/creditflowresearch/application/ArticlesAndStats/Articles/constants';
import { useFiltersContext } from '@framework/ui/Organisms/Filters/FiltersContext';
import { useCheckboxerContext } from '@framework/ui/Organisms/Filters/Checkboxer/CheckboxerContext';
import { CFR_ARTICLES } from '@/creditflowresearch/ui/components/creditFlow/articles/FiltersButton/constants';
import {
  EntityType,
  getDateFromInCurrentTimeZone,
  getDateToInCurrentTimeZone,
  mappingEntityAndSubentity,
} from './helpers';
import getQueryParamsForFilters from '@/creditflowresearch/application/helpers/getQueryParamsForFilters';
import { useHeaderMenuContext } from '@/creditflowresearch/application/HeaderMenu/HeaderMenuContext';
import { IQueryParams } from '@/creditflowresearch/domain/vo/IQueryParams';
import { removeQueryParams } from '@framework/router';
import { addQueryParams, cleanQueryParams } from '@framework/router/nextRouter';
import { getFiltersPayload } from '@/creditflowresearch/ui/components/creditFlow/articles/helpers';
import { TCheckboxesState } from '@/creditflowresearch/domain/vo/TCheckboxesState';
import { IRegion } from '@/creditflowresearch/domain/vo/Filters/IRegion';
import { ISector } from '@/creditflowresearch/domain/vo/Filters/ISector';
import { IIndustry } from '@/creditflowresearch/domain/vo/Filters/IIndustry';
import { prepareProductsListForFilters, prepareRegionsListForFilters } from '@/creditflowresearch/application/helpers/filters';
import { useUserPlanContext } from '@/creditflowresearch/application/User/UserPlan/UserPlanContext';
import getRegionsByCountries from '@/creditflowresearch/application/helpers/getRegionsByCountries';
import { getArticlesBasePath } from './getArticlesBasePath';
import { UTC_TIME_ZONE } from '@framework/timezone/constants';
import { getISODateFromUnix } from '@/creditflowresearch/application/ArticlesAndStats/Articles/helpers';

const useActions = (dispatch) => {
  const container = getContainer();
  const creditFlowResearchRepository = container.get<CreditFlowResearchRepository>(CreditFlowResearchRepository);
  const { regions: regionsList, products: productsList } = useHeaderMenuContext();
  const { query, push, asPath } = useRouter();
  const { regions = '', products = '', productType, articleType }:IQueryParams = query;
  const basePath = getArticlesBasePath(asPath);
  const {
    products: accessibleProductsIds,
    regions: accessibleRegionsIds,
  } = useUserPlanContext();

  const { filtersUpdate, getFilters } = useFiltersContext();
  const { checkboxes } = useCheckboxerContext();

  const filters: IMarketArticlesFilterPayload = getFilters(CFR_ARTICLES);
  const checkboxesState: TCheckboxesState = checkboxes;

  const payloadChange = (payload) => dispatch(createAction(FILTER_PAYLOAD, payload));
  const initialFiltersChange = (payload) => dispatch(createAction(INITIAL_FILTERS, payload));
  const appliedFiltersChange = (payload) => dispatch(createAction(FILTER_APPLY, payload));

  const setMinMaxDatePeriod = (dateFrom, dateTo) => {
    dispatch(createAction(FILTER_MIN_DATE_FROM, dateFrom));
    dispatch(createAction(FILTER_MAX_DATE_TO, dateTo));
  };

  const getBoundaryFilters = async (
    payload: IMarketArticlesFilterPayload,
  ): Promise<IMarketArticlesFilterBoundaryResponse> => {
    dispatch(createAction(GET_FILTERS));

    const filter = {
      ...payload,
      dateFrom: getISODateFromUnix(payload.dateFrom),
      dateTo: getISODateFromUnix(payload.dateTo),
    };

    try {
      let response = await creditFlowResearchRepository.getArticlesFilters({ filter });
      const regionList = prepareRegionsListForFilters(response.filter.regionList || [], accessibleRegionsIds);
      const productList = prepareProductsListForFilters(response.filter.productList || [], accessibleProductsIds);

      response = {
        ...response,
        filter: {
          ...response.filter,
          dateFrom: getISODateFromUnix(response.filter.dateFrom),
          dateTo: getISODateFromUnix(response.filter.dateTo),
          regionList,
          productList,
        },
      };

      dispatch(createAction(GET_FILTERS_SUCCESS, response));

      return response;
    } catch (error) {
      dispatch(createAction(GET_FILTERS_ERROR));
      Logger.warn({ message: 'Failed to fetch boundary filters, using default state', error });

      const defaultResponse: IMarketArticlesFilterBoundaryResponse = {
        hash: '',
        filter: {
          dateFrom: null,
          dateTo: null,
          dealList: { allowed: [], selected: [] },
          parentIssuerList: { allowed: [], selected: [] },
          regionList: prepareRegionsListForFilters([], accessibleRegionsIds),
          sectorList: [],
          industryList: [],
          productList: prepareProductsListForFilters([], accessibleProductsIds),
        },
      };

      return defaultResponse;
    }
  };

  const applyFilter = async () => {
    const payloadFilters = getFiltersPayload({ filters, checkboxesState });
    const isInitialFilters = isEqual(initialArticlesFilterState, payloadFilters);
    const appliedFilters = isInitialFilters ? {} : payloadFilters;

    payloadChange(payloadFilters);
    appliedFiltersChange(appliedFilters);
  };

  const fetchFilter = async () => {
    const payloadFilters = getFiltersPayload({ filters, checkboxesState });
    const { hash, filter: { regionList } } = await getBoundaryFilters(payloadFilters);
    const isInitialFilters = isEqual(initialArticlesFilterState, payloadFilters);

    if (isInitialFilters) {
      const queryParams = removeQueryParams(
        query,
        ['dateFrom', 'dateTo', 'hash', 'articleType', 'productType'],
      );
      const pathname = `${ basePath }/${ articleType }-${ productType }`;

      push({ pathname, query: queryParams }, undefined, { shallow: true });
    }

    if (hash && !isInitialFilters) {
      const regionsIds = [
        ...filters.includedRegionIds,
        ...getRegionsByCountries(filters.includedCountryIds, regionList),
      ];
      const queryParams = getQueryParamsForFilters({
        regionsList,
        productsList,
        regionsIds,
        productsIds: filters.includedProductIds,
        dateFrom: filters.dateFrom,
        dateTo: filters.dateTo,
        currentProducts: products,
        currentRegions: regions,
      });
      const newQuery = removeQueryParams(
        addQueryParams(query, { ...queryParams, hash }),
        ['articleType', 'productType'],
      );
      const pathname = `${ basePath }/${ articleType }-${ productType }`;

      push({ pathname, query: cleanQueryParams(newQuery) }, undefined, { shallow: true });
    }
  };

  const initFilters = async (
    filtersPayload: IMarketArticlesFilterPayload,
    filtersState: IMarketArticlesFilterPayload = initialArticlesFilterState,
  ) => {
    try {
      const response = await getBoundaryFilters(filtersPayload);
      const articlesFilter = response.filter;
      const [includedRegionIds, includedCountryIds] = mappingEntityAndSubentity<IRegion>(
        filtersState.includedRegionIds,
        articlesFilter.regionList,
        EntityType.Region,
      );
      const [includedSectorIds, includedSubsectorIds] = mappingEntityAndSubentity<ISector>(
        filtersState.includedSectorIds,
        articlesFilter.sectorList,
        EntityType.Sector,
      );
      const [includedIndustryIds, includedSubindustryIds] = mappingEntityAndSubentity<IIndustry>(
        filtersState.includedIndustryIds,
        articlesFilter.industryList,
        EntityType.Industry,
      );

      const initialFilters = {
        ...initialArticlesFilterState,
        includedRegionIds,
        includedCountryIds: [...filtersState.includedCountryIds, ...includedCountryIds],
        includedSectorIds,
        includedSubsectorIds: [...filtersState.includedSubsectorIds, ...includedSubsectorIds],
        includedIndustryIds,
        includedSubindustryIds: [...filtersState.includedSubindustryIds, ...includedSubindustryIds],
      };

      const appliedFilters = {
        ...filtersState,
        includedRegionIds,
        includedCountryIds: [...filtersState.includedCountryIds, ...includedCountryIds],
        includedSectorIds,
        includedSubsectorIds: [...filtersState.includedSubsectorIds, ...includedSubsectorIds],
        includedIndustryIds,
        includedSubindustryIds: [...filtersState.includedSubindustryIds, ...includedSubindustryIds],
      };

      initialFiltersChange(initialFilters);

      filtersUpdate(CFR_ARTICLES, appliedFilters);
      appliedFiltersChange(appliedFilters);

      const dateFrom = getDateFromInCurrentTimeZone(articlesFilter.dateFrom, UTC_TIME_ZONE);
      const dateTo = getDateToInCurrentTimeZone(articlesFilter.dateTo, UTC_TIME_ZONE);

      setMinMaxDatePeriod(dateFrom, dateTo);
    } catch (error) {
      Logger.warn({ message: 'Failed to set min/max date period', error });
    }
  };

  const resetFilter = async () => {
    const initialState = {
      ...initialArticlesFilterState,
      includedProductIds: filters.includedProductIds,
    };
    const payloadFilters = getFiltersPayload({ filters: initialState, checkboxesState });

    payloadChange(initialState);
    await initFilters(payloadFilters, initialState);

    const queryParams = removeQueryParams(
      query,
      ['dateFrom', 'dateTo', 'hash', 'articleType', 'productType', 'regions'],
    );
    const pathname = `${ basePath }/${ articleType }-${ productType }`;

    push({ pathname, query: queryParams }, undefined, { shallow: true });
  };

  return {
    initFilters,
    applyFilter,
    fetchFilter,
    payloadChange,
    resetFilter,
    getBoundaryFilters,
  };
};

export default useActions;
